import React, { useState } from "react";
import { 
  Box, 
  Typography, 
  TextField,
  Button,
  Paper,
  CircularProgress,
  InputAdornment,
  IconButton,
  Link,
  Alert,
  Container,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  Checkbox,
  GlobalStyles
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useClerk, useSignUp } from "@clerk/clerk-react";
import Header from "../common/Header";
import Footer from "../common/Footer";
import { Visibility, VisibilityOff, PersonAddAlt1Outlined } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { isCaptchaError, getCaptchaErrorMessage } from "../../utils/captchaUtils";

// API URL for the backend
const API_URL = process.env.REACT_APP_API_URL;

const SignUpPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectPath = searchParams.get('redirect') || '/dashboard';

  // Form states
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [verificationStep, setVerificationStep] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  // Verification states
  const [verificationCode, setVerificationCode] = useState('');
  const [isResending, setIsResending] = useState(false);

  // Clerk hooks
  const { signUp, isLoaded: isSignUpLoaded } = useSignUp();
  const clerk = useClerk();

  // Toggle password visibility
  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  // Toggle confirm password visibility
  const handleToggleConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Validate the registration form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    let isValid = true;

    if (!firstName.trim()) {
      errors.firstName = 'First name is required';
      isValid = false;
    }

    if (!lastName.trim()) {
      errors.lastName = 'Last name is required';
      isValid = false;
    }

    if (!email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      errors.email = 'Invalid email address';
      isValid = false;
    }

    if (!password) {
      errors.password = 'Password is required';
      isValid = false;
    } else if (password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
      isValid = false;
    }

    if (confirmPassword !== password) {
      errors.confirmPassword = 'Passwords do not match';
      isValid = false;
    }

    if (!agreedToTerms) {
      errors.terms = 'You must agree to the Terms of Service and Privacy Policy';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  // Handle account creation
  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!signUp) {
      setError('System error: Unable to initialize the registration process.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Create a new user with email and password
      // Do not include firstName/lastName in the initial create call
      await signUp.create({
        emailAddress: email,
        password,
      });

      // Attempt to verify the email
      try {
        const signUpResponse = await signUp.prepareEmailAddressVerification({
          strategy: "email_code"
        });

        // Try to set firstName, lastName after account is created
        try {
          // Log the exact parameters being sent
          console.log('About to update user with parameters:', { firstName, lastName });
          
          // Enforce camelCase for parameter names
          await signUp.update({
            firstName: firstName,
            lastName: lastName,
          });
        } catch (nameError) {
          console.error("Error setting name, but continuing:", nameError);
          // Continue even if setting the name fails
        }

        if (signUpResponse.status === 'complete') {
          // If email verification isn't required or is instant
          completeSignUp();
        } else {
          // Show verification screen
          setVerificationStep(true);
          // Reset loading state when switching to verification step
          setLoading(false);
          // Reset verification code when showing verification step
          setVerificationCode('');
        }
      } catch (verifyError) {
        console.error("Error during email verification:", verifyError);
        // If verification fails but account was created, still move forward
        completeSignUp();
      }
    } catch (error: any) {
      console.error('Account creation error:', error);
      
      
      if (error.errors && error.errors.length > 0) {
        const pwnedPasswordError = error.errors.find(
          (err: any) => err.code === "form_password_pwned"
        );
        
        if (pwnedPasswordError) {
          setError('This password has been found in a data breach. Please use a different password for your security.');
          setFormErrors({
            ...formErrors,
            password: 'Please choose a more secure password',
            confirmPassword: 'Please choose a more secure password'
          });
        } else if (isCaptchaError(error)) {
          setError(getCaptchaErrorMessage(error));
        } else {
          setError('Error creating account. Please check your information and try again.');
        }
      } else if (isCaptchaError(error)) {
        setError(getCaptchaErrorMessage(error));
      } else {
        setError('Error creating account. Please check your information and try again.');
      }
      
      setLoading(false);
    }
  };

  // Function to update the user name in our backend
  const updateUserNameInBackend = async (firstName: string, lastName: string) => {
    try {
      // Get authentication token from Clerk
      if (!clerk.session) {
        console.error("No active session found");
        return;
      }
      const token = await clerk.session.getToken();
      
      console.log('Updating user name in backend:', { firstName, lastName });
      
      // Call our backend API to update the user name
      const response = await fetch(`${API_URL}/users/update-name`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Clerk-User-Id': clerk.user?.id || '',
          'X-Clerk-Email': clerk.user?.primaryEmailAddress?.emailAddress || '',
          'X-Clerk-First-Name': firstName,
          'X-Clerk-Last-Name': lastName
        },
        body: JSON.stringify({ firstName, lastName })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update user name: ${response.statusText}`);
      }
      
      console.log('User name updated successfully in backend');
    } catch (error) {
      console.error('Error updating user name in backend:', error);
      // Don't throw error to prevent blocking the signup process
    }
  };

  // Complete the sign up process and redirect
  const completeSignUp = async () => {
    try {
      if (!signUp) {
        // If we don't have a sign up object but we have a session, we're already logged in
        if (clerk.session) {
          console.log("No sign up object found but session exists, redirecting to dashboard");
          
          // Call the API to update the user's name in our backend
          await updateUserNameInBackend(firstName, lastName);
          
          navigate(redirectPath);
          return;
        }
        
        setError('System error: Unable to complete registration.');
        setLoading(false);
        return;
      }

      // Show processing message
      setError('Processing your registration, please wait...');

      try {
        // First check if we already have a session - this can happen if verification 
        // completed but the sign up object was cleared
        if (clerk.session) {
          console.log("Active session already exists, no need to complete sign up again");
          
          // Call the API to update the user's name in our backend
          await updateUserNameInBackend(firstName, lastName);
          
          // Redirect directly to dashboard without delay
          navigate(redirectPath);
          return;
        }
        
        // Attempt to complete the signup using an empty code
        // This works when email verification is not required or has been completed
        try {
          const { status, createdSessionId } = await signUp.attemptEmailAddressVerification({
            code: ""
          });
          
          if (status === 'complete' && createdSessionId) {
            // Set the newly created session as active
            await clerk.setActive({ session: createdSessionId });
            
            // Call the API to update the user's name in our backend
            await updateUserNameInBackend(firstName, lastName);
            
            // Redirect directly to dashboard without delay
            navigate(redirectPath);
          } else {
            setError('Unable to complete registration. Please try again.');
            setLoading(false);
          }
        } catch (verificationError: any) {
          console.error('Error during verification completion:', verificationError);
          
          // Check if the error suggests we're already logged in or verification succeeded
          if (verificationError.message && (
            verificationError.message.includes('already verified') ||
            verificationError.message.includes('already active') ||
            verificationError.message.includes('session exists') ||
            verificationError.message.includes('No sign up')
          )) {
            // If the verification likely succeeded, try to navigate anyway
            console.log("Verification error suggests user might be verified, attempting to navigate");
            navigate(redirectPath);
            return;
          }
          
          setError('Error completing registration. Please try again or refresh the page.');
          setLoading(false);
        }
      } catch (error) {
        console.error('Error completing sign up:', error);
        setError('Error completing registration. Please try again.');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error in complete sign up:', error);
      setError('Error completing registration. Please try again.');
      setLoading(false);
    }
  };

  // Handle email verification
  const handleVerifyEmail = async (code: string) => {
    if (!signUp) {
      // First check if we have a session - verification may have succeeded
      if (clerk.session) {
        console.log("Active session found, proceeding to dashboard");
        navigate(redirectPath);
        return;
      }
      
      setError('System error: Unable to verify email.');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      console.log(`Attempting to verify email with code: ${code}`);
      
      // Try to update firstName, lastName again if needed
      try {
        await signUp.update({
          firstName,
          lastName,
        });
      } catch (nameError) {
        console.error("Error setting name during verification, but continuing:", nameError);
      }
      
      const response = await signUp.attemptEmailAddressVerification({
        code,
      });

      console.log('Verification response:', response);
      if (response.status === 'complete') {
        // If verification was successful and created a session ID
        if (response.createdSessionId) {
          try {
            await clerk.setActive({ session: response.createdSessionId });
            navigate(redirectPath);
            return;
          } catch (sessionError) {
            console.error("Error activating session:", sessionError);
          }
        }
        
        // Complete sign up after verification
        completeSignUp();
      } else {
        setError('Verification failed. Please try again.');
        setLoading(false);
      }
    } catch (error: any) {
      console.error('Verification error:', error);
      
      // If the error indicates the session is already active or verification already happened
      if (error.message && (
          error.message.includes('already verified') || 
          error.message.includes('already active') ||
          error.message.includes('session exists') ||
          error.message.includes('No sign up')
        )) {
        console.log('Session appears to be active despite verification error. Attempting to redirect.');
        
        // If we have an active session, proceed to dashboard
        if (clerk.session) {
          navigate(redirectPath);
          return;
        }
        
        // Try to simply navigate to dashboard since we might already be logged in
        // but the session state hasn't updated in the client yet
        try {
          console.log("No active session found but verification may have succeeded, navigating to dashboard");
          navigate(redirectPath);
        } catch (sessionError) {
          console.error('Failed to recover from verification error:', sessionError);
          setError('There was an issue during verification. Please try refreshing the page or logging in.');
        }
      } else {
        setError('Error verifying email. Please try again.');
      }
      setLoading(false);
    }
  };

  // Navigate to login page
  const handleLogin = () => {
    navigate(`/login${redirectPath !== '/dashboard' ? `?redirect=${redirectPath}` : ''}`);
  };

  // Render verification step
  const renderVerificationStep = () => {
    // Handle code submission
    const handleSubmitCode = () => {
      if (verificationCode.length === 6) {
        handleVerifyEmail(verificationCode);
      } else {
        setError('Please enter a valid 6-digit verification code.');
      }
    };

    // Resend verification code
    const handleResendCode = async () => {
      if (!signUp) {
        setError('System error: Unable to resend verification code.');
        return;
      }

      setIsResending(true);
      setError(null);

      try {
        await signUp.prepareEmailAddressVerification({
          strategy: "email_code"
        });
        
        // Show success message
        setError('A new verification code has been sent to your email.');
        // Clear the verification code input
        setVerificationCode('');
      } catch (error) {
        console.error('Error resending verification code:', error);
        setError('Error sending new verification code. Please try again.');
      } finally {
        setIsResending(false);
      }
    };

    // Handle pressing Enter in the verification code field
    const handleKeyPress = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && verificationCode.length === 6 && !loading) {
        handleSubmitCode();
      }
    };

    return (
      <Box>
        <Typography variant="h5" gutterBottom fontWeight="600">
          Verify Your Email
        </Typography>
        <Typography variant="body1" paragraph>
          We've sent a verification code to <strong>{email}</strong>. Please enter it below to continue.
        </Typography>
        
        {error && (
          <Alert 
            severity={error.includes('has been sent') ? 'success' : 'error'} 
            sx={{ mb: 3 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}
        
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Verification Code"
            variant="outlined"
            sx={{ mb: 2 }}
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Enter 6-digit code"
            inputProps={{ 
              maxLength: 6, 
              inputMode: 'numeric',
              pattern: '[0-9]*' 
            }}
            autoFocus
          />
          
          <Button
            variant="contained"
            fullWidth
            onClick={handleSubmitCode}
            disabled={loading || verificationCode.length !== 6}
            sx={{
              backgroundColor: "#5D5FEF",
              color: "#fff",
              py: 1.5,
              mb: 2,
              '&:hover': {
                backgroundColor: "#4A4BD5",
              }
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify Code'}
          </Button>
        </Box>
        
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            Didn't receive a code? Check your spam folder or:
          </Typography>
          <Button 
            variant="text" 
            onClick={handleResendCode}
            disabled={isResending}
            sx={{ color: '#5D5FEF' }}
          >
            {isResending ? 'Sending...' : 'Resend Verification Code'}
          </Button>
        </Box>
      </Box>
    );
  };

  // Render registration form
  const renderRegistrationForm = () => (
    <Box component="form" onSubmit={handleCreateAccount}>
      <Typography variant="h5" gutterBottom fontWeight="600">
        Create Your Free Account
      </Typography>
      <Typography variant="body1" paragraph sx={{ mb: 3 }}>
        
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            fullWidth
            label="First Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            error={!!formErrors.firstName}
            helperText={formErrors.firstName}
            variant="outlined"
            sx={{ flex: 1 }}
          />
          <TextField
            fullWidth
            label="Last Name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            error={!!formErrors.lastName}
            helperText={formErrors.lastName}
            variant="outlined"
            sx={{ flex: 1 }}
          />
        </Box>
        
        <TextField
          fullWidth
          label="Email Address"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={!!formErrors.email}
          helperText={formErrors.email}
          variant="outlined"
          sx={{ mb: 2 }}
        />
        
        <TextField
          fullWidth
          label="Password"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={!!formErrors.password}
          helperText={formErrors.password}
          variant="outlined"
          sx={{ mb: 2 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleTogglePassword}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        
        <TextField
          fullWidth
          label="Confirm Password"
          type={showConfirmPassword ? 'text' : 'password'}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          error={!!formErrors.confirmPassword}
          helperText={formErrors.confirmPassword}
          variant="outlined"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleToggleConfirmPassword}
                  edge="end"
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Terms and Conditions Checkbox */}
      <FormControlLabel
        control={
          <Checkbox
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e.target.checked)}
            color="primary"
          />
        }
        label={
          <Typography variant="body2">
            I agree to the{' '}
            <Link component={RouterLink} to="/terms-of-service" target="_blank" color="primary">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link component={RouterLink} to="/privacy-policy" target="_blank" color="primary">
              Privacy Policy
            </Link>
          </Typography>
        }
        sx={{ mb: 2 }}
      />
      {formErrors.terms && (
        <Typography color="error" variant="caption" sx={{ mt: -1, mb: 2, display: 'block' }}>
          {formErrors.terms}
        </Typography>
      )}

      {/* CAPTCHA Widget - Required for Clerk's bot protection */}
      <div 
        id="clerk-captcha" 
        data-cl-theme="auto"
        data-cl-size="normal"
        data-cl-language="auto"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '16px'
        }}
      />

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={loading}
        sx={{
          backgroundColor: "#5D5FEF",
          color: "#fff",
          py: 1.5,
          mb: 2,
          '&:hover': {
            backgroundColor: "#4A4BD5",
          }
        }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : 'Create Account'}
      </Button>

      <Divider sx={{ my: 2 }}>
        <Typography variant="body2" color="textSecondary">
          OR
        </Typography>
      </Divider>

      <Box sx={{ textAlign: 'center' }}>
        <Link 
          component="button" 
          variant="body2" 
          onClick={handleLogin}
          sx={{ color: '#5D5FEF', textDecoration: 'none', fontWeight: 500 }}
        >
          Already have an account? Sign In
        </Link>
      </Box>
    </Box>
  );

  if (!isSignUpLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box 
      sx={{
        width: "100%",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#f4f6f8"
      }}
    >
      <GlobalStyles 
        styles={{
          '#clerk-captcha:empty': {
            display: 'none !important',
            margin: '0 !important',
            padding: '0 !important'
          }
        }}
      />
      <Header />

      <Container maxWidth="sm" sx={{ py: 6, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        {/* Welcome Card */}
        <Card 
        sx={{
            mb: 4, 
            background: 'linear-gradient(135deg, #5D5FEF 0%, #4A4BD5 100%)',
            color: 'white',
            boxShadow: '0px 8px 20px rgba(93, 95, 239, 0.25)',
            borderRadius: 2
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <PersonAddAlt1Outlined sx={{ fontSize: 40, mr: 2 }} />
              <Typography variant="h4" fontWeight="bold">Welcome to Tranzcribe!</Typography>
        </Box>
            <Typography variant="body1">
              Create your account and start transforming your transcriptions into effective communications.
            </Typography>
          </CardContent>
        </Card>

        <Paper 
          elevation={3} 
          sx={{ p: { xs: 3, md: 4 }, borderRadius: 2 }}
        >
          {verificationStep ? renderVerificationStep() : renderRegistrationForm()}
        </Paper>
      </Container>

      <Footer />
    </Box>
  );
};

export default SignUpPage;
